import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Fade,
  Alert,
  InputAdornment,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import LocationCityOutlinedIcon from '@mui/icons-material/LocationCityOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Navbar from '../components/Navbar.js';
import ImageCropUpload from '../components/ImageCropUpload.js';
import UsernamePasswordFields from '../components/UsernamePasswordFields.js';
import StripePaymentForm from '../components/StripePaymentForm.js'
import { ENDPOINTS, apiCall } from '../utils/api.js';
import PhoneField, { defaultCountry, composeE164 } from '../components/PhoneField.js';
import {
  getCountryOptions,
  getStateOptions,
  getCityOptions,
  NO_STATE_PLACEHOLDER,
} from '../utils/locationTree.js';

const COUNTRY_OPTIONS = getCountryOptions();

// Mirrors Organization.TYPE choices on the backend.
const ORG_TYPES = [
  ['restaurant', 'Restaurant'], ['clinic', 'Clinic'], ['doctor', 'Doctor'],
  ['company', 'Company'], ['store', 'Store'], ['bank', 'Bank'],
  ['school', 'School'], ['factory', 'Factory'], ['others', 'Others'],
];

const DESCRIPTION_MAX = 500;

// Per-link draft of the signup form, so a reload / revisiting the onboarding link resumes where
// the business left off instead of making them retype everything.
// Deliberately NOT persisted: password/confirm (secrets) and the cropped photo (large base64 that
// would risk blowing the sessionStorage quota and silently killing all persistence).
const DRAFT_KEY = (ref) => `sqip:onboardingDraft:${ref}`;

const readDraft = (ref) => {
  if (!ref) return null;
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY(ref));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const clearDraft = (ref) => {
  try { sessionStorage.removeItem(DRAFT_KEY(ref)); } catch { /* unavailable */ }
};   // mirrors main/models.py DESCRIPTION_MAX_LENGTH

const EMPTY_FORM = {
  businessName: '',
  publicPhone: '',
  country: '',
  state: '',
  city: '',
  storefrontAddress: '',
  pincode: '',
  businessDescription: '',
  website: '',
  orgType: '',
  // page 2
  firstName: '',
  lastName: '',
  privatePhone: '',
  email: '',
};

function validateStep1(f) {
  const errs = {};
  if (!f.businessName.trim()) errs.businessName = 'Business name is required.';
  if (!f.publicPhone.trim()) errs.publicPhone = 'Public phone number is required.';
  else if (!/^[+\d\s\-().]{6,20}$/.test(f.publicPhone.trim())) errs.publicPhone = 'Please enter a valid phone number.';
  if (!f.storefrontAddress.trim()) errs.storefrontAddress = 'Business address is required.';
  if (!f.country) errs.country = 'Country is required.';
  if (!f.state) errs.state = 'State / Province is required.';
  if (!f.city) errs.city = 'City is required.';
  if (!f.orgType) errs.orgType = 'Business category is required.';
  return errs;
}

function validateStep2(f) {
  const errs = {};
  if (!f.firstName.trim()) errs.firstName = 'First name is required.';
  if (!f.lastName.trim()) errs.lastName = 'Last name is required.';
  if (!f.privatePhone.trim()) errs.privatePhone = 'Your personal phone number is required — it is how you sign in.';
  else if (!/^[+\d\s\-().]{6,20}$/.test(f.privatePhone.trim())) errs.privatePhone = 'Please enter a valid phone number.';
  if (!f.email.trim()) errs.email = 'Email is required.';
  else if (!/^\S+@\S+\.\S+$/.test(f.email.trim())) errs.email = 'Please enter a valid email address.';
  return errs;
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const { reference } = useParams();
  // Restored synchronously so the server prefill below merges on top without clobbering
  // anything the business already typed.
  const [step, setStep] = useState(() => {
    const d = readDraft(reference);
    return d && Number.isInteger(d.step) ? d.step : 0;
  }); // 0 = public store info, 1 = private account info
  const [form, setForm] = useState(() => {
    const d = readDraft(reference);
    return d && d.form ? { ...EMPTY_FORM, ...d.form } : EMPTY_FORM;
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitStatus, setSubmitStatus] = useState(null); // null | 'loading' | 'success' | 'error'
  const [submitError, setSubmitError] = useState('');
  const [prefill, setPrefill] = useState(null);
  const [trialOffered, setTrialOffered] = useState(true);
  const [publicPhoneCountry, setPublicPhoneCountry] = useState(defaultCountry);
  const [privatePhoneCountry, setPrivatePhoneCountry] = useState(defaultCountry);
  const [picture, setPicture] = useState('');
  const [credsNote, setCredsNote] = useState('');
  // optional credentials
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [credsValid, setCredsValid] = useState(true);
  // Subscription package/tier selection (fetched from /api/packages/).
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState('');
  const [packagesError, setPackagesError] = useState('');

  // Persist the draft (form + which page they're on) so a reload, a browser back, or clicking
  // the onboarding link again resumes exactly where they were.
  useEffect(() => {
    if (!reference || submitStatus === 'success') return;
    try {
      sessionStorage.setItem(DRAFT_KEY(reference), JSON.stringify({ form, step }));
    } catch { /* quota/unavailable — persistence is a convenience, never required */ }
  }, [reference, form, step, submitStatus]);

  // Load checkout prefill for this reference (business name + whether a trial is offered).
  useEffect(() => {
    if (!reference) return;
    apiCall(ENDPOINTS.ONBOARDING_CHECKOUT(reference))
      .then((d) => {
        // Deferred setState (strict set-state-in-effect rule).
        setTimeout(() => {
          setPrefill(d);
          if (typeof d.trial_offered === 'boolean') setTrialOffered(d.trial_offered);
          // "Completed" means a live Stripe subscription — NOT merely that an org row
          // exists, which the pay-first path creates BEFORE payment. Showing success off
          // org-existence told people "you're all set" after a FAILED payment.
          if (d.already_completed) {
            setSubmitStatus('success');
            clearDraft(reference);   // genuinely finished — don't resurrect the form later
          } else if (d.org_created) {
            // Account provisioned but payment never completed — drop them back on the
            // payment step so they can retry, instead of re-filling the whole form.
            setStep(2);
          }
          // The "credentials not applied" note is set at submit but lost on the full-page reload
          // Stripe triggers on return — restore it so the success screen still shows it.
          try {
            const savedNote = sessionStorage.getItem(`sqip:credsNote:${reference}`);
            if (savedNote) setCredsNote(savedNote);
          } catch { /* sessionStorage unavailable */ }
          setForm((prev) => ({
            ...prev,
            businessName: prev.businessName || d.business_name || '',
            firstName: prev.firstName || d.contact_first_name || '',
            email: prev.email || d.contact_email || '',
          }));
        }, 0);
      })
      .catch(() => {});
  }, [reference]);

  // Load the available packages; default the selection to a ?package= query param if present.
  useEffect(() => {
    apiCall(ENDPOINTS.PACKAGES)
      .then((d) => {
        // Tolerate the shapes this endpoint might return: a bare array, a DRF
        // paginated {results: []}, or {packages: []}. Guessing only one of these
        // is what silently emptied the plan picker and skipped the payment step.
        const list = Array.isArray(d) ? d : (d?.packages || d?.results || []);
        setTimeout(() => {
          setPackages(list);
          // Prefer an explicit ?package= param, then a tier the user picked on the
          // pricing page (stashed in localStorage), then the first package.
          const wanted = new URLSearchParams(window.location.search).get('package')
            || localStorage.getItem('sqip:selectedPackage');
          const match = list.find((p) => p.slug === wanted);
          setSelectedPackage((prev) => prev || (match ? match.slug : (list[0] ? list[0].slug : '')));
          if (list.length === 0) setPackagesError('Could not load plans. Please refresh and try again.');
        }, 0);
      })
      .catch(() => {
        // Never swallow this: with no packages the payment step can't run at all.
        setTimeout(() => setPackagesError('Could not load plans. Please refresh and try again.'), 0);
      });
  }, []);

  // Stripe's embedded Checkout redirects to the session's return_url when the
  // customer finishes, carrying ?session_id=... Ask the backend what happened:
  // 'complete' -> done, 'open' -> they bailed or the payment failed, so remount.
  // Account state itself is set by the checkout.session.completed webhook.
  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get('session_id');
    if (!sessionId) return;
    apiCall(`${ENDPOINTS.CHECKOUT_SESSION_STATUS}?session_id=${encodeURIComponent(sessionId)}`)
      .then((d) => {
        setTimeout(() => {
          if (d?.status === 'complete') {
            setSubmitStatus('success');
            clearDraft(reference);
          } else {
            setStep(2);
            setSubmitError('Checkout was not completed. Please try again.');
            setSubmitStatus('error');
          }
        }, 0);
      })
      .catch(() => {});
  }, []);

  const stateOptions = useMemo(
    () => (form.country ? getStateOptions(form.country) : []),
    [form.country],
  );
  const cityOptions = useMemo(() => {
    if (!form.country || !form.state) return [];
    const selectedState = stateOptions.find((s) => s.label === form.state);
    return getCityOptions(form.country, selectedState?.isoCode ?? null);
  }, [form.country, form.state, stateOptions]);
  const selectedPkgObj = useMemo(
    () => packages.find((p) => p.slug === selectedPackage) || null,
    [packages, selectedPackage],
  );
  // Stripe needs a real customer address to resolve location (even with tax off, it's used
  // for card-network/AVS checks) — built from the step-0 business address fields. The postal
  // code is included: it's the strongest signal both for Stripe's lookup and for our own
  // geocoding, which drives distance/"near me" ranking in search.
  const billingAddress = useMemo(() => ({
    line1: form.storefrontAddress.trim(),
    city: form.city || '',
    state: form.state === NO_STATE_PLACEHOLDER ? '' : (form.state || ''),
    postal_code: form.pincode.trim(),
    country: form.country || '',
  }), [form.storefrontAddress, form.city, form.state, form.pincode, form.country]);

  const validators = [validateStep1, validateStep2];

  const handleChange = (field) => (e) => {
    const val = e.target.value;
    let reset = {};
    if (field === 'country') reset = { state: '', city: '' };
    if (field === 'state') reset = { city: '' };
    setForm((prev) => ({ ...prev, [field]: val, ...reset }));
    if (touched[field]) {
      setErrors((prev) => ({ ...prev, [field]: validators[step]({ ...form, [field]: val, ...reset })[field] }));
    }
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setErrors((prev) => ({ ...prev, [field]: validators[step](form)[field] }));
  };

  const goNext = () => {
    const errs = validateStep1(form);
    setErrors(errs);
    setTouched({ businessName: true, publicPhone: true, storefrontAddress: true, country: true, state: true, city: true, orgType: true });
    if (Object.keys(errs).length > 0) return;
    setStep(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goBack = () => {
    setStep(0);
    setErrors({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goBackToAccountInfo = () => {
    setStep(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    const errs = validateStep2(form);
    setErrors(errs);
    setTouched((prev) => ({ ...prev, firstName: true, lastName: true, privatePhone: true, email: true }));
    if (Object.keys(errs).length > 0) return;

    // Optional credentials: both-or-neither, and the fields must validate.
    if (username.trim() || password) {
      if (!username.trim() || !password) {
        setSubmitError('Provide both a username and a password, or leave both blank.');
        setSubmitStatus('error');
        return;
      }
      if (!credsValid) {
        setSubmitError('Fix the username/password fields before continuing.');
        setSubmitStatus('error');
        return;
      }
    }

    if (!reference) {
      setSubmitError('This checkout link is invalid. Please use the link from your email.');
      setSubmitStatus('error');
      return;
    }

    setSubmitStatus('loading');
    setSubmitError('');

    try {
      const res = await apiCall(ENDPOINTS.ONBOARDING_CHECKOUT(reference), {
        method: 'POST',
        body: JSON.stringify({
          legal_name: form.businessName.trim(),
          address_line1: form.storefrontAddress.trim(),
          country: form.country,
          state: form.state === NO_STATE_PLACEHOLDER ? '' : form.state,
          city: form.city,
          pincode: form.pincode.trim(),
          description: form.businessDescription.trim(),
          portfolio_site: form.website.trim(),
          phone_number: composeE164(publicPhoneCountry, form.publicPhone),
          type: form.orgType || 'others',
          display_picture_base64: picture || '',
          first_name: form.firstName.trim(),
          last_name: form.lastName.trim(),
          email: form.email.trim(),
          private_phone: composeE164(privatePhoneCountry, form.privatePhone),
          username: username.trim(),
          password: password,
          package: selectedPackage || '',
        }),
      });

      if (res && res.credentials_note) {
        setCredsNote(res.credentials_note);
        // Persist so it survives the full-page reload Stripe does on return to this page.
        try { sessionStorage.setItem(`sqip:credsNote:${reference}`, res.credentials_note); } catch { /* ignore */ }
      }

      // Always continue to the payment step. Previously this fell through to the
      // success screen whenever selectedPkgObj was null (e.g. the plan list failed
      // to load), which skipped Stripe entirely and looked like a broken flow.
      setSubmitStatus(null);
      setStep(2);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      // 409 = the org already exists (e.g. they went Back then Next after creating it). Unless the
      // subscription is genuinely done (a Stripe subscription is linked), resume the payment step so
      // the payment page reappears — for a trial OR pay-first, whenever the card isn't captured yet.
      if (err && err.status === 409) {
        if (err.body && err.body.subscribed) {
          setSubmitStatus('success');
          clearDraft(reference);
          return;
        }
        setSubmitStatus(null);
        setStep(2);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      const msg = (err && (err.message || (err.body && err.body.message))) || 'Something went wrong. Please try again.';
      setSubmitError(msg);
      setSubmitStatus('error');
      // Field-level errors from page 2 (username/password) keep the user on step 2.
    }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: 'background.paper' },
  };

  // ── Success screen ────────────────────────────────────────────────────────
  if (submitStatus === 'success') {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <Navbar />
        <Fade in timeout={400}>
          <Box
            sx={{
              maxWidth: 520, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 8, sm: 12 },
              textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 1 }} />
            <Typography variant="h5" fontWeight={800}>
              You're all set!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
              {(() => {
                // Prefer the plan the backend actually recorded (survives the full-page reload
                // from Stripe, which would otherwise reset the picker to the default tier).
                const planName = prefill?.package_name || selectedPkgObj?.name;
                const forBiz = prefill?.business_name ? ` for ${prefill.business_name}` : '';
                return planName
                  ? `Your account${forBiz} is ready and your ${planName} plan is active. Check your email for a secure sign-in link to your dashboard.`
                  : `Your account${forBiz} is ready. Check your email for a secure sign-in link to your dashboard.`;
              })()}
            </Typography>
            {credsNote && <Alert severity="info" sx={{ borderRadius: 2, textAlign: 'left' }}>{credsNote}</Alert>}
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/')}
              sx={{ mt: 1, borderRadius: 2.5, fontWeight: 600 }}
            >
              Back to home
            </Button>
          </Box>
        </Fade>
      </Box>
    );
  }

  // ── Main 2-step form ──────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Navbar />

      <Box
        sx={{
          maxWidth: 720, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 4, sm: 6 },
          display: 'flex', flexDirection: 'column', gap: 4,
        }}
      >
        {/* ── Header + progress ─────────────────────────────────────────── */}
        <Fade in timeout={400}>
          <Box>
            <Chip
              label={`Account registration — Step ${step + 1} of 3`}
              size="small" color="primary" variant="outlined"
              sx={{ mb: 2, fontWeight: 600, fontSize: 12 }}
            />
            <Typography variant="h4" fontWeight={900} sx={{ letterSpacing: '-0.02em', mb: 1.5, lineHeight: 1.2 }}>
              {step === 0 ? 'Your public store info.' : step === 1 ? 'Your private account info.' : 'Set up billing.'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75, maxWidth: 540 }}>
              {step === 0
                ? 'This is what customers see when they find your business on sqip.'
                : step === 1
                ? 'This is your personal login information — it is never shown publicly.'
                : 'Your card is required to activate the plan you chose — you will not be charged before your trial (if any) ends.'}
            </Typography>
            <Stepper activeStep={step} sx={{ mt: 3, maxWidth: 480 }}>
              <Step><StepLabel>Public store info</StepLabel></Step>
              <Step><StepLabel>Private account info</StepLabel></Step>
              <Step><StepLabel>Payment</StepLabel></Step>
            </Stepper>
          </Box>
        </Fade>

        {/* ── Form card ─────────────────────────────────────────────────── */}
        <Fade in timeout={500} key={step}>
          <Box
            sx={{
              bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
              borderRadius: 4, px: { xs: 2.5, sm: 4 }, py: { xs: 3, sm: 4 },
              display: 'flex', flexDirection: 'column', gap: 3,
            }}
          >
            {step === 0 ? (
              <>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Business / organization name"
                    value={form.businessName}
                    onChange={handleChange('businessName')}
                    onBlur={handleBlur('businessName')}
                    error={Boolean(touched.businessName && errors.businessName)}
                    helperText={touched.businessName && errors.businessName}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start"><GavelOutlinedIcon fontSize="small" color="action" /></InputAdornment>
                      ),
                    }}
                    sx={inputSx}
                  />

                  <Box>
                    <PhoneField
                      country={publicPhoneCountry}
                      setCountry={setPublicPhoneCountry}
                      phone={form.publicPhone}
                      setPhone={(v) => handleChange('publicPhone')({ target: { value: v } })}
                      onBlur={handleBlur('publicPhone')}
                      label="Public business phone number"
                      error={Boolean(touched.publicPhone && errors.publicPhone)}
                      helperText={touched.publicPhone && errors.publicPhone}
                    />
                    <Alert severity="warning" sx={{ mt: 1, borderRadius: 2 }}>
                      ⚠️ This phone number will be <strong>PUBLIC</strong> and visible in search results.
                      Do not use your personal number.
                    </Alert>
                  </Box>

                  <TextField
                    fullWidth
                    label="Business address"
                    value={form.storefrontAddress}
                    onChange={handleChange('storefrontAddress')}
                    onBlur={handleBlur('storefrontAddress')}
                    error={Boolean(touched.storefrontAddress && errors.storefrontAddress)}
                    helperText={touched.storefrontAddress && errors.storefrontAddress}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start"><StorefrontOutlinedIcon fontSize="small" color="action" /></InputAdornment>
                      ),
                    }}
                    sx={inputSx}
                  />

                  <TextField
                    fullWidth
                    label="Postal / ZIP code"
                    value={form.pincode}
                    onChange={handleChange('pincode')}
                    helperText="Helps customers find you — used for map accuracy and nearby search."
                    sx={inputSx}
                  />

                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="About your business (optional)"
                    placeholder="e.g. Family-run optical shop — eye tests, frames and repairs."
                    value={form.businessDescription}
                    onChange={(e) => setForm((f) => ({
                      ...f, businessDescription: e.target.value.slice(0, DESCRIPTION_MAX),
                    }))}
                    helperText={`${form.businessDescription.length}/${DESCRIPTION_MAX} — shown on your card in search results`}
                    FormHelperTextProps={{ sx: { textAlign: 'right', mr: 0 } }}
                    sx={inputSx}
                  />

                  <TextField
                    fullWidth
                    label="Website (optional)"
                    placeholder="https://"
                    value={form.website}
                    onChange={handleChange('website')}
                    sx={inputSx}
                  />

                  <TextField
                    select fullWidth label="Country"
                    value={form.country}
                    onChange={handleChange('country')}
                    onBlur={handleBlur('country')}
                    error={Boolean(touched.country && errors.country)}
                    helperText={touched.country && errors.country}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start"><PublicOutlinedIcon fontSize="small" color="action" /></InputAdornment>
                      ),
                    }}
                    sx={inputSx}
                  >
                    {COUNTRY_OPTIONS.map((opt) => (
                      <MenuItem key={opt.isoCode} value={opt.isoCode}>{opt.label}</MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select fullWidth label="State / Province"
                    value={form.state}
                    onChange={handleChange('state')}
                    onBlur={handleBlur('state')}
                    error={Boolean(touched.state && errors.state)}
                    helperText={touched.state && errors.state}
                    disabled={!form.country}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start"><MapOutlinedIcon fontSize="small" color="action" /></InputAdornment>
                      ),
                    }}
                    sx={inputSx}
                  >
                    {stateOptions.map((opt) => (
                      <MenuItem key={opt.label} value={opt.label}>{opt.label}</MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select fullWidth label="City"
                    value={form.city}
                    onChange={handleChange('city')}
                    onBlur={handleBlur('city')}
                    error={Boolean(touched.city && errors.city)}
                    helperText={touched.city && errors.city}
                    disabled={!form.state}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start"><LocationCityOutlinedIcon fontSize="small" color="action" /></InputAdornment>
                      ),
                    }}
                    sx={inputSx}
                  >
                    {cityOptions.length > 0 ? (
                      cityOptions.map((opt) => (
                        <MenuItem key={opt.label} value={opt.label}>{opt.label}</MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled value="">No cities available</MenuItem>
                    )}
                  </TextField>

                  <TextField
                    select fullWidth label="Business category"
                    value={form.orgType}
                    onChange={handleChange('orgType')}
                    onBlur={handleBlur('orgType')}
                    error={Boolean(touched.orgType && errors.orgType)}
                    helperText={touched.orgType && errors.orgType}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start"><CategoryOutlinedIcon fontSize="small" color="action" /></InputAdornment>
                      ),
                    }}
                    sx={inputSx}
                  >
                    {ORG_TYPES.map(([value, label]) => (
                      <MenuItem key={value} value={value}>{label}</MenuItem>
                    ))}
                  </TextField>

                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      Profile photo (optional)
                    </Typography>
                    <ImageCropUpload value={picture} onChange={setPicture} />
                  </Box>
                </Box>

                <Button
                  variant="contained" color="primary" size="large"
                  endIcon={<ArrowForwardIcon />}
                  onClick={goNext}
                  sx={{ borderRadius: 2.5, fontWeight: 700, color: '#fff', py: 1.5,
                        alignSelf: { xs: 'stretch', sm: 'flex-end' }, minWidth: 160 }}
                >
                  Next
                </Button>
              </>
            ) : step === 1 ? (
              <>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <TextField
                      fullWidth label="First name"
                      value={form.firstName}
                      onChange={handleChange('firstName')}
                      onBlur={handleBlur('firstName')}
                      error={Boolean(touched.firstName && errors.firstName)}
                      helperText={touched.firstName && errors.firstName}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start"><PersonOutlineIcon fontSize="small" color="action" /></InputAdornment>
                        ),
                      }}
                      sx={inputSx}
                    />
                    <TextField
                      fullWidth label="Last name"
                      value={form.lastName}
                      onChange={handleChange('lastName')}
                      onBlur={handleBlur('lastName')}
                      error={Boolean(touched.lastName && errors.lastName)}
                      helperText={touched.lastName && errors.lastName}
                      sx={inputSx}
                    />
                  </Box>

                  <Box>
                    <PhoneField
                      country={privatePhoneCountry}
                      setCountry={setPrivatePhoneCountry}
                      phone={form.privatePhone}
                      setPhone={(v) => handleChange('privatePhone')({ target: { value: v } })}
                      onBlur={handleBlur('privatePhone')}
                      label="Personal phone number"
                      error={Boolean(touched.privatePhone && errors.privatePhone)}
                      helperText={touched.privatePhone && errors.privatePhone}
                    />
                    <Alert severity="info" sx={{ mt: 1, borderRadius: 2 }}>
                      ℹ️ This number is used for login only and will never be shown publicly.
                    </Alert>
                  </Box>

                  <TextField
                    fullWidth label="Email"
                    value={form.email}
                    onChange={handleChange('email')}
                    onBlur={handleBlur('email')}
                    error={Boolean(touched.email && errors.email)}
                    helperText={touched.email && errors.email}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start"><EmailOutlinedIcon fontSize="small" color="action" /></InputAdornment>
                      ),
                    }}
                    sx={inputSx}
                  />

                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                      Choose your plan
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                      Pick the package that fits your business. You can change it later.
                    </Typography>
                    {packagesError ? (
                      <Alert severity="error" sx={{ borderRadius: 2 }}>{packagesError}</Alert>
                    ) : packages.length === 0 ? (
                      <CircularProgress size={20} />
                    ) : (
                      <TextField
                        select fullWidth label="Package" value={selectedPackage}
                        onChange={(e) => setSelectedPackage(e.target.value)} sx={inputSx}
                        helperText={
                          (packages.find((p) => p.slug === selectedPackage) || {}).sms_allowed === false
                            ? 'This plan includes email notifications only (no SMS).'
                            : ' '
                        }
                      >
                        {packages.map((p) => (
                          <MenuItem key={p.slug} value={p.slug}>
                            {p.name} — ${p.monthly_price}/mo{p.sms_allowed === false ? ' · email only' : ''}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Box>

                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                      Set a username & password (optional)
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                      Sign in with a username and password instead of a phone code. You can also set this later.
                    </Typography>
                    <UsernamePasswordFields
                      username={username} setUsername={setUsername}
                      password={password} setPassword={setPassword}
                      confirm={confirm} setConfirm={setConfirm}
                      onValidity={setCredsValid}
                    />
                  </Box>
                </Box>

                {submitStatus === 'error' && (
                  <Alert severity="error" sx={{ borderRadius: 2 }}>{submitError}</Alert>
                )}

                <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column-reverse', sm: 'row' }, justifyContent: 'space-between' }}>
                  <Button
                    variant="outlined" size="large" startIcon={<ArrowBackIcon />} onClick={goBack}
                    disabled={submitStatus === 'loading'}
                    sx={{ borderRadius: 2.5, fontWeight: 600 }}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained" color="primary" size="large"
                    endIcon={submitStatus === 'loading' ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIcon />}
                    onClick={handleSubmit}
                    disabled={submitStatus === 'loading'}
                    sx={{ borderRadius: 2.5, fontWeight: 700, color: '#fff', py: 1.5, minWidth: 180 }}
                  >
                    {submitStatus === 'loading' ? 'Creating account…' : 'Continue to payment'}
                  </Button>
                </Box>
              </>
            ) : (
              <>
                {credsNote && (
                  <Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>{credsNote}</Alert>
                )}
                {selectedPkgObj ? (
                  <>
                    <Box
                      sx={{
                        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                        flexWrap: 'wrap', gap: 1, pb: 1,
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={700}>
                        {selectedPkgObj.name} plan
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={700} color="text.secondary">
                        ${selectedPkgObj.monthly_price}/mo
                      </Typography>
                    </Box>

                    <StripePaymentForm
                      reference={reference}
                      packageName={selectedPkgObj.name}
                      address={billingAddress}
                      onError={(msg) => { setSubmitError(msg); setSubmitStatus('error'); }}
                    />
                  </>
                ) : (
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>
                    No plan is selected, so payment can't start. Go back and choose a plan.
                  </Alert>
                )}

                {submitStatus === 'error' && submitError && (
                  <Alert severity="error" sx={{ borderRadius: 2 }}>{submitError}</Alert>
                )}

                <Button
                  variant="text" size="small" startIcon={<ArrowBackIcon />}
                  onClick={goBackToAccountInfo}
                  sx={{ alignSelf: 'flex-start', fontWeight: 600, mt: 1 }}
                >
                  Edit account details
                </Button>
              </>
            )}
          </Box>
        </Fade>

        {/* ── Footer note ──────────────────────────────────────────────── */}
        <Fade in timeout={600}>
          <Typography variant="caption" color="text.disabled" sx={{ textAlign: 'center' }}>
            By registering, you agree to our{' '}
            <Typography
              component="a" href="/legal/business-terms" variant="caption" color="primary"
              sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              Business Terms of Service
            </Typography>
            . Your information is never shared with third parties.
          </Typography>
        </Fade>
      </Box>
    </Box>
  );
}
